import type { ReactNode } from 'react';

import type { ReportCellContext, ReportColumn } from '@/types/report-definition';
import { formatBusinessDate } from '@/utils/date-utils';
import { formatMoneyCents } from '@/utils/format-money';

/**
 * Pure column-mapping helpers shared by `ReportView`. Kept free of React /
 * expo-router imports so they can be unit-tested directly without rendering the
 * whole report component (see tests/frontend/components/report-columns.spec.ts).
 */
export interface DataTableColumn<TRow> {
	id: string;
	name: string;
	selector: (row: TRow) => unknown;
	cell?: (row: TRow) => ReactNode;
	sortable: boolean;
	sortFunction?: (a: TRow, b: TRow) => number;
	right: boolean;
	center: boolean;
	grow?: number;
	width?: string;
	wrap: boolean;
}

/**
 * Default cell-value formatter applied when the column doesn't supply a custom
 * `cell` renderer. Mirrors the xlsx-side format choices so on-screen and
 * exported values stay consistent.
 */
export function formatForDisplay(value: unknown, format: ReportColumn<unknown>['format']): string {
	if (value == null || value === '') return '';
	if (format === 'money' && typeof value === 'number') return formatMoneyCents(value);
	if (format === 'date' && typeof value === 'number') return formatBusinessDate(value);
	if (format === 'integer' && typeof value === 'number') return value.toLocaleString();
	if (typeof value === 'number') return String(value);
	if (typeof value === 'string') return value;
	if (typeof value === 'boolean') return value ? 'yes' : '';
	return String(value);
}

/** Context handed to a `cell` renderer when the caller supplies none (tests, ad-hoc use). */
const EMPTY_CELL_CONTEXT: ReportCellContext = { visibleColumnIds: new Set<string>() };

export function toDataTableColumn<TRow>(
	c: ReportColumn<TRow>,
	ctx: ReportCellContext = EMPTY_CELL_CONTEXT,
): DataTableColumn<TRow> {
	const right = c.align === 'right' || (!c.align && (c.format === 'money' || c.format === 'integer'));
	const center = c.align === 'center';
	const baseSort = c.sortAccessor ?? ((row: TRow) => {
		const v = c.accessor(row);
		if (typeof v === 'number' || typeof v === 'string') return v;
		if (v == null) return '';
		return String(v);
	});

	return {
		id: c.id,
		name: c.header,
		selector: (row) => formatForDisplay(c.accessor(row), c.format),
		cell: c.cell ? (row) => c.cell!(row, ctx) : undefined,
		sortable: c.sortable !== false,
		sortFunction: c.sortable === false ? undefined : (a, b) => {
			const av = baseSort(a);
			const bv = baseSort(b);
			if (typeof av === 'number' && typeof bv === 'number') return av - bv;
			return String(av).localeCompare(String(bv));
		},
		right,
		center,
		grow: c.grow,
		width: c.width ? `${c.width}px` : undefined,
		wrap: Boolean(c.wrap),
	};
}

/**
 * Map report columns → DataTable columns.
 *
 * Sortability is driven entirely by each column's `sortable` flag (default on).
 * Under server-sort mode (ReportView's `serverSort`) the DataTable still emits
 * an `onSort` callback per click — the PAGE maps the clicked column id to a
 * server sort key. Note we deliberately do NOT try to "disable" a subset of
 * columns here: react-data-table-component treats a column as sortable when
 * EITHER `sortable` is true OR a `sortFunction` is present, so clearing only the
 * flag wouldn't actually block the header. Server-sorted reports therefore make
 * EVERY displayed column server-sortable instead of gating a subset.
 */
export function buildTableColumns<TRow>(columns: ReportColumn<TRow>[]): DataTableColumn<TRow>[] {
	// The visible set IS this column list — a cell that asks "is field X already a
	// column?" is asking about what's on screen, which is exactly what was passed in.
	const ctx: ReportCellContext = { visibleColumnIds: new Set(columns.map((c) => c.id)) };
	return columns.map((c) => toDataTableColumn(c, ctx));
}
